import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { QuotationResponseDto } from "../../shared/dtos/quotation/QuotationResponseDto";
import { QuotationSummaryDto } from "../../shared/dtos/quotation/QuotationSummaryDto";
import { SaleType } from "../../shared/enums/SaleType";
import {
  getInitialCrudState,
  generateCrudReducers,
} from "../utils/crudFactory";
import { InvoiceCartItem } from "./invoice";

interface QuotationInitialState {
  cartItems: InvoiceCartItem[];
  selectedCustomer: Record<string, unknown> | null;
  saleType: SaleType;
  cartSubtotal: number;
}

const calculateSubtotal = (items: InvoiceCartItem[]): number =>
  items.reduce(
    (acc, item) =>
      acc +
      (Number(item.price) - Number(item.discount || 0)) * Number(item.qty),
    0,
  );

const loadInitialState = (): QuotationInitialState => {
  try {
    const items = JSON.parse(localStorage.getItem("quote_items") || "[]");
    const customer = JSON.parse(
      localStorage.getItem("quote_customer") || "null",
    );
    const saleType =
      (localStorage.getItem("quote_saleType") as SaleType) || SaleType.RETAIL;
    return {
      cartItems: items as InvoiceCartItem[],
      selectedCustomer: customer as Record<string, unknown> | null,
      saleType,
      cartSubtotal: calculateSubtotal(items),
    };
  } catch (e) {
    return {
      cartItems: [],
      selectedCustomer: null,
      saleType: SaleType.RETAIL,
      cartSubtotal: 0,
    };
  }
};

const initialState = {
  ...getInitialCrudState<QuotationResponseDto, QuotationSummaryDto>(),
  ...loadInitialState(),

  loadingStatusUpdate: false,
  loadingConvert: false,
  loadingPdf: false,
  loadingEmail: false,

  errorStatusUpdate: null as string | null,
  errorConvert: null as string | null,
  errorPdf: null as string | null,
  errorEmail: null as string | null,

  pdfUrl: null as string | null,
};

export const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    ...generateCrudReducers<QuotationResponseDto, QuotationSummaryDto>(),

    setLoadingStatusUpdate(state) {
      state.loadingStatusUpdate = true;
      state.errorStatusUpdate = null;
    },
    setLoadingConvert(state) {
      state.loadingConvert = true;
      state.errorConvert = null;
    },
    setLoadingPdf(state) {
      state.loadingPdf = true;
      state.errorPdf = null;
    },
    setLoadingEmail(state) {
      state.loadingEmail = true;
      state.errorEmail = null;
    },

    setErrorStatusUpdate(state, action: PayloadAction<string | null>) {
      state.loadingStatusUpdate = false;
      state.errorStatusUpdate = action.payload;
    },
    setErrorConvert(state, action: PayloadAction<string | null>) {
      state.loadingConvert = false;
      state.errorConvert = action.payload;
    },
    setErrorPdf(state, action: PayloadAction<string | null>) {
      state.loadingPdf = false;
      state.errorPdf = action.payload;
    },
    setErrorEmail(state, action: PayloadAction<string | null>) {
      state.loadingEmail = false;
      state.errorEmail = action.payload;
    },

    setPdfUrl(state, action: PayloadAction<string>) {
      state.loadingPdf = false;
      state.pdfUrl = action.payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setEmailSuccess(state) {
      state.loadingEmail = false;
    },

    // --- Cart Reducers ---
    setQuoteSaleType(state, action: PayloadAction<SaleType>) {
      state.saleType = action.payload;
      localStorage.setItem("quote_saleType", action.payload);
    },
    setQuoteCustomer(
      state,
      action: PayloadAction<Record<string, unknown> | null>,
    ) {
      state.selectedCustomer = action.payload as Draft<
        Record<string, unknown>
      > | null;
      localStorage.setItem("quote_customer", JSON.stringify(action.payload));
    },
    quoteItemAdd(state, action: PayloadAction<InvoiceCartItem>) {
      const existing = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (existing) {
        existing.qty += action.payload.qty;
      } else {
        state.cartItems.push(action.payload as Draft<InvoiceCartItem>);
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("quote_items", JSON.stringify(state.cartItems));
    },
    quoteItemUpdateFields(
      state,
      action: PayloadAction<{
        itemId: string;
        updates: Partial<InvoiceCartItem>;
      }>,
    ) {
      const item = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (item) {
        if (action.payload.updates.qty !== undefined)
          item.qty = Number(action.payload.updates.qty);
        if (action.payload.updates.price !== undefined)
          item.price = Number(action.payload.updates.price);
        if (action.payload.updates.discount !== undefined)
          item.discount = Number(action.payload.updates.discount);
        if (item.qty <= 0)
          state.cartItems = state.cartItems.filter(
            (i) => i.itemId !== action.payload.itemId,
          );
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("quote_items", JSON.stringify(state.cartItems));
    },
    quoteItemRemove(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter(
        (i) => i.itemId !== action.payload,
      );
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("quote_items", JSON.stringify(state.cartItems));
    },
    clearQuoteCart(state) {
      state.cartItems = [];
      state.cartSubtotal = 0;
      state.selectedCustomer = null;
      localStorage.removeItem("quote_items");
      localStorage.removeItem("quote_customer");
    },
  },
});

export const quotationActions = quotationSlice.actions;
export default quotationSlice.reducer;
